import { useEffect, useState } from "react";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses } from "../theme";
import { apiErrorMessage, fetchInvoiceRecipients, type InvoiceRecipients } from "../services/invoicesService";

// ===========================================================================
// «A quién se le manda la factura» en el formulario del negocio.
// ===========================================================================
// Los destinatarios ya no se escriben a mano: son las personas que el cliente
// tiene registradas en el CRM con un cargo de Finanzas, Administración o Dueños
// y dirección. Aquí solo se muestran; se agregan o cambian en CRM → Empresas.
// ===========================================================================

const AREA_LABEL: Record<string, string> = {
  finanzas: "Finanzas",
  administracion: "Administración",
  direccion: "Dueños y dirección",
  operaciones: "Operaciones",
  comercial: "Comercial",
  ti: "TI",
  staff: "Staff",
};

export default function DealBillingRecipients({ dealId }: { dealId?: string | null }) {
  const { isDarkMode } = useTheme();
  const themeClasses = buildThemeClasses(isDarkMode);
  const [data, setData] = useState<InvoiceRecipients | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!dealId) return;
    let alive = true;
    fetchInvoiceRecipients(dealId)
      .then((next) => alive && setData(next))
      .catch((err) => alive && setError(apiErrorMessage(err)));
    return () => {
      alive = false;
    };
  }, [dealId]);

  const box = `rounded-lg border px-3 py-2 text-sm ${themeClasses.border} ${themeClasses.text}`;
  const hint = (
    <p className={`mt-2 text-[11px] ${themeClasses.textSecondary}`}>
      La factura se manda a las personas del cliente con cargo de Finanzas, Administración o Dueños y dirección. Se agregan o cambian en CRM → Empresas → la
      empresa del cliente → Personas.
    </p>
  );

  if (!dealId) {
    return (
      <div className={box}>
        <p className={themeClasses.textSecondary}>Guarda el negocio para ver aquí a quién de su empresa le llegará la factura.</p>
        {hint}
      </div>
    );
  }
  if (error) return <div className={`${box} text-red-500`}>{error}</div>;
  if (!data) return <div className={`${box} ${themeClasses.textSecondary}`}>Cargando destinatarios…</div>;

  return (
    <div className={box} data-testid="deal-billing-recipients">
      {data.people.length === 0 ? (
        <p className="text-amber-600">
          <i className="ri-alert-line mr-1" />
          {data.client || "El cliente"} no tiene personas registradas en el CRM: la factura se generará pero no se enviará a nadie.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {data.people.map((person, index) => (
            <li key={person.id ?? `${person.email}-${index}`} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
              <span>
                <span className="font-medium">{person.name || person.email || "Sin nombre"}</span>
                <span className={`ml-2 text-xs ${themeClasses.textSecondary}`}>
                  {[person.role, person.area ? AREA_LABEL[person.area] : null].filter(Boolean).join(" · ") || "Sin cargo"}
                </span>
                <span className={`block text-xs ${themeClasses.textSecondary}`}>{person.email || "Sin correo"}</span>
              </span>
              <span
                className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${
                  person.receives ? "bg-emerald-500/15 text-emerald-600" : "bg-gray-400/15 text-gray-500"
                }`}
              >
                {person.receives ? "Recibe la factura" : person.email ? "No la recibe" : "Sin correo"}
              </span>
            </li>
          ))}
        </ul>
      )}
      {data.people.length > 0 && data.emails.length === 0 && (
        <p className="mt-2 text-xs text-amber-600">
          <i className="ri-alert-line mr-1" />
          Nadie tiene correo: la factura se generará pero no se enviará.
        </p>
      )}
      {data.legacyEmails.length > 0 && (
        <p className={`mt-2 text-xs ${themeClasses.textSecondary}`}>
          Mientras el cliente no tenga personas con correo, se usa lo que el negocio tenía guardado: {data.legacyEmails.join(", ")}.
        </p>
      )}
      {hint}
    </div>
  );
}
